import { computed, ref, watch } from 'vue';

const safeJsonParse = (value, fallback) => {
    try {
        return JSON.parse(value);
    }
    catch (e) {
        return fallback;
    }
};

const normalizeKey = (key) => (typeof key === 'string' ? key : '');

export const useTableColumns = (params) => {
    const {
        columns,
        summaryLabelField,
        tableKey,
        userId,
        enableColumnVisibility,
        enableColumnWidthPersistence,
        defaultHiddenKeys
    } = params;

    const storageBaseKey = computed(() => {
        const uid = userId.value || '';
        const tKey = tableKey.value || '';
        return `${uid}__${tKey}`;
    });

    const widthStorageKey = computed(() => `table_col_width_${storageBaseKey.value}`);
    const visibleStorageKey = computed(() => `table_col_visible_${storageBaseKey.value}`);

    const columnWidths = ref({});
    const visibleKeySet = ref(new Set());

    const loadPersistedWidths = () => {
        if (!enableColumnWidthPersistence.value) return;
        const raw = localStorage.getItem(widthStorageKey.value);
        columnWidths.value = safeJsonParse(raw, {}) || {};
    };

    const persistWidths = () => {
        if (!enableColumnWidthPersistence.value) return;
        localStorage.setItem(widthStorageKey.value, JSON.stringify(columnWidths.value || {}));
    };

    const loadPersistedVisibility = () => {
        if (!enableColumnVisibility.value) return;
        const raw = localStorage.getItem(visibleStorageKey.value);
        const keys = safeJsonParse(raw, null);
        if (Array.isArray(keys)) {
            visibleKeySet.value = new Set(keys.map(normalizeKey).filter(Boolean));
            return;
        }

        const initialVisible = new Set();
        (columns.value || []).forEach((col) => {
            const key = normalizeKey(col && col.key);
            if (!key) return;
            if ((defaultHiddenKeys.value || []).includes(key)) return;
            initialVisible.add(key);
        });
        visibleKeySet.value = initialVisible;
        localStorage.setItem(visibleStorageKey.value, JSON.stringify([...initialVisible]));
    };

    const persistVisibility = () => {
        if (!enableColumnVisibility.value) return;
        localStorage.setItem(visibleStorageKey.value, JSON.stringify([...visibleKeySet.value]));
    };

    watch([widthStorageKey, enableColumnWidthPersistence], () => {
        loadPersistedWidths();
    }, { immediate: true });

    watch([visibleStorageKey, enableColumnVisibility], () => {
        loadPersistedVisibility();
    }, { immediate: true });

    watch(columns, () => {
        if (!enableColumnVisibility.value) return;
        const current = new Set((columns.value || []).map((c) => normalizeKey(c && c.key)).filter(Boolean));
        const next = new Set(visibleKeySet.value);
        const hiddenKeys = defaultHiddenKeys.value || [];
        let changed = false;

        hiddenKeys.forEach((k) => {
            if (current.has(k) && next.has(k)) {
                next.delete(k);
                changed = true;
            }
        });

        Array.from(next).forEach((k) => {
            if (!current.has(k)) {
                next.delete(k);
                changed = true;
            }
        });

        current.forEach((k) => {
            if (!next.has(k) && !hiddenKeys.includes(k)) {
                next.add(k);
                changed = true;
            }
        });

        if (changed) {
            visibleKeySet.value = next;
            persistVisibility();
        }
    }, { deep: true });

    const allLeafColumns = computed(() => (columns.value || []).filter(Boolean));

    const visibleLeafColumns = computed(() => {
        const cols = allLeafColumns.value;
        if (!enableColumnVisibility.value) return cols;

        return cols.filter((col) => {
            const key = normalizeKey(col && col.key);
            if (!key) return true;
            return visibleKeySet.value.has(key);
        });
    });

    const processedColumns = computed(() => {
        const labelField = summaryLabelField.value || '';
        const widths = columnWidths.value || {};

        return (visibleLeafColumns.value || []).map((column) => {
            const key = normalizeKey(column && column.key);
            const persistedWidth = key && widths[key] ? widths[key] : undefined;
            const width = typeof persistedWidth === 'number' ? persistedWidth : column.width;
            return {
                ...column,
                width,
                resizable: enableColumnWidthPersistence.value ? true : column.resizable,
                className: labelField && key === labelField ? 'summary-label' : (column.className || '')
            };
        });
    });

    const handleColumnWidthResize = (newWidth, oldWidth, column, event) => {
        if (!enableColumnWidthPersistence.value) return;
        const key = normalizeKey(column && column.key);
        if (!key) return;
        if (typeof newWidth !== 'number' || !isFinite(newWidth) || newWidth <= 0) return;
        columnWidths.value = {
            ...(columnWidths.value || {}),
            [key]: Math.round(newWidth)
        };
        persistWidths();
    };

    const allColumnsForSetting = computed(() => {
        return (columns.value || [])
            .filter((c) => c && normalizeKey(c.key))
            .map((c) => ({ key: normalizeKey(c.key), title: c.title || normalizeKey(c.key) }));
    });

    const isAllVisible = computed(() => {
        if (!enableColumnVisibility.value) return true;
        const allKeys = allColumnsForSetting.value.map((c) => c.key);
        return allKeys.length > 0 && allKeys.every((k) => visibleKeySet.value.has(k));
    });

    const isNoneVisible = computed(() => {
        if (!enableColumnVisibility.value) return false;
        const allKeys = allColumnsForSetting.value.map((c) => c.key);
        return allKeys.length > 0 && allKeys.every((k) => !visibleKeySet.value.has(k));
    });

    const setAllVisible = () => {
        if (!enableColumnVisibility.value) return;
        visibleKeySet.value = new Set(allColumnsForSetting.value.map((c) => c.key));
        persistVisibility();
    };

    const setNoneVisible = () => {
        if (!enableColumnVisibility.value) return;
        visibleKeySet.value = new Set();
        persistVisibility();
    };

    const resetVisibility = () => {
        if (!enableColumnVisibility.value) return;
        localStorage.removeItem(visibleStorageKey.value);
        loadPersistedVisibility();
    };

    const toggleVisible = (key) => {
        if (!enableColumnVisibility.value) return;
        const k = normalizeKey(key);
        if (!k) return;
        const next = new Set(visibleKeySet.value);
        if (next.has(k)) next.delete(k);
        else next.add(k);
        visibleKeySet.value = next;
        persistVisibility();
    };

    const resetWidths = () => {
        if (!enableColumnWidthPersistence.value) return;
        localStorage.removeItem(widthStorageKey.value);
        loadPersistedWidths();
    };

    return {
        processedColumns,
        allColumnsForSetting,
        visibleKeySet,
        isAllVisible,
        isNoneVisible,
        setAllVisible,
        setNoneVisible,
        resetVisibility,
        toggleVisible,
        handleColumnWidthResize,
        resetWidths
    };
};
